import { useState, useEffect, useRef } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Gift, Sparkles, Crown, X, Trophy } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/App";
import { Gift3DAnimation } from "./GiftAnimation3D";
import { GiftPicker as GiftPickerV2 } from "./GiftPickerV2";

// ===== ADE GIFT DEFINITIONS =====
// Each gift has: image, name, cost, animation tier (1-7), and animation config
// COSTS ARE IN ADE COINS (backend is source of truth for exact values)
// MORE EXPENSIVE = MORE SPECTACULAR ANIMATION
const ADE_GIFTS = [
  {
    id: "cuore",
    name: "Cuore",
    image: "/images/gifts/cuore.png",
    cost: 100,
    tier: 1,
    description: "Un dolce pensiero",
    animation: {
      type: "float",
      duration: 2500,
      particles: 12,
      scale: 1.2,
    }
  },
  {
    id: "rosa",
    name: "Rosa",
    image: "/images/gifts/rosa.png",
    cost: 500,
    tier: 2,
    description: "Romantico e elegante",
    animation: {
      type: "petals",
      duration: 3500,
      particles: 25,
      scale: 1.4,
    }
  },
  {
    id: "stella",
    name: "Stella",
    image: "/images/gifts/stella.png",
    cost: 1000,
    tier: 2,
    description: "Brilla come te",
    animation: {
      type: "shooting",
      duration: 3000,
      particles: 35,
      scale: 1.5,
    }
  },
  {
    id: "fuoco",
    name: "Fuoco",
    image: "/images/gifts/fuoco.png",
    cost: 5000,
    tier: 3,
    description: "Passione ardente",
    animation: {
      type: "flames",
      duration: 4000,
      particles: 50,
      scale: 1.6,
    }
  },
  {
    id: "fulmine",
    name: "Fulmine",
    image: "/images/gifts/fulmine.png",
    cost: 10000,
    tier: 3,
    description: "Energia pura",
    animation: {
      type: "electric",
      duration: 3500,
      particles: 40,
      scale: 1.7,
    }
  },
  {
    id: "diamante",
    name: "Diamante",
    image: "/images/gifts/diamante.png",
    cost: 50000,
    tier: 4,
    description: "Prezioso e raro",
    animation: {
      type: "sparkle",
      duration: 5000,
      particles: 60,
      scale: 2.0,
    }
  },
  {
    id: "corona",
    name: "Corona",
    image: "/images/gifts/corona.png",
    cost: 100000,
    tier: 5,
    description: "Il regalo dei re",
    premium: 1,
    animation: {
      type: "royal",
      duration: 5500,
      particles: 80,
      scale: 2.2,
    }
  },
  // === PREMIUM GIFTS (Golden Ring Lv.2) ===
  {
    id: "trono",
    name: "Trono",
    image: "/images/gifts/trono.png",
    cost: 500000,
    tier: 6,
    description: "Potere assoluto",
    premium: 2,
    animation: {
      type: "throne",
      duration: 7000,
      particles: 100,
      scale: 2.5,
    }
  },
  {
    id: "supercar",
    name: "Supercar",
    image: "/images/gifts/supercar.png",
    cost: 1000000,
    tier: 6,
    description: "Velocità e lusso",
    premium: 2,
    animation: {
      type: "speed",
      duration: 6000,
      particles: 90,
      scale: 2.6,
    }
  },
  {
    id: "fenice",
    name: "Fenice",
    image: "/images/gifts/fenice.png",
    cost: 2500000,
    tier: 6,
    description: "Rinascita leggendaria",
    premium: 2,
    animation: {
      type: "phoenix",
      duration: 8000,
      particles: 120,
      scale: 2.8,
    }
  },
  // === ULTIMATE PREMIUM GIFTS (Golden Ring Lv.3) ===
  {
    id: "castello",
    name: "Castello",
    image: "/images/gifts/castello.png",
    cost: 5000000,
    tier: 7,
    description: "Regno incantato",
    premium: 3,
    animation: {
      type: "castle",
      duration: 9000,
      particles: 150,
      scale: 3.0,
    }
  },
  {
    id: "universo",
    name: "Universo",
    image: "/images/gifts/universo.png",
    cost: 10000000,
    tier: 7,
    description: "L'infinito in dono",
    premium: 3,
    animation: {
      type: "cosmic",
      duration: 10000,
      particles: 200,
      scale: 3.5,
    }
  },
];

// Format coins for display - NO € equivalency shown
const formatCoins = (amount) => {
  if (amount >= 1000000) {
    const m = amount / 1000000;
    return m % 1 === 0 ? `${m.toFixed(0)}M` : `${m.toFixed(1)}M`;
  }
  if (amount >= 1000) {
    const k = amount / 1000;
    return k % 1 === 0 ? `${k.toFixed(0)}K` : `${k.toFixed(1)}K`;
  }
  return amount.toString();
};

// ===== GIFT ANIMATION OVERLAY COMPONENT =====
// Full-screen animation when gift is sent
const GiftAnimationOverlay = ({ gift, isVisible, onComplete }) => {
  const [phase, setPhase] = useState(0);
  const containerRef = useRef(null);
  
  useEffect(() => {
    if (!isVisible || !gift) return;
    
    // Animation phases
    setPhase(1); // Entrance
    setTimeout(() => setPhase(2), 500); // Main animation
    setTimeout(() => setPhase(3), gift.animation.duration - 500); // Exit
    setTimeout(() => {
      setPhase(0);
      onComplete?.();
    }, gift.animation.duration);
  }, [isVisible, gift, onComplete]);
  
  if (!isVisible || !gift) return null;
  
  // Generate particles based on tier
  const renderParticles = () => {
    const particles = [];
    const count = gift.animation.particles;
    
    for (let i = 0; i < count; i++) {
      const delay = Math.random() * 1000;
      const duration = 1000 + Math.random() * 2000;
      const startX = Math.random() * 100;
      const startY = Math.random() * 100;
      
      let particleStyle = {};
      let particleClass = "";
      
      switch (gift.animation.type) {
        case "petals":
          particleStyle = {
            left: `${startX}%`,
            top: "-10%",
            animationDelay: `${delay}ms`,
            animationDuration: `${duration}ms`,
          };
          particleClass = "particle-petal";
          break;
        case "flames":
          particleStyle = {
            left: `${30 + Math.random() * 40}%`,
            bottom: "10%",
            animationDelay: `${delay}ms`,
            animationDuration: `${800 + Math.random() * 400}ms`,
          };
          particleClass = "particle-flame";
          break;
        case "electric":
          particleStyle = {
            left: `${startX}%`,
            top: `${startY}%`,
            animationDelay: `${delay}ms`,
            animationDuration: `${300 + Math.random() * 300}ms`,
          };
          particleClass = "particle-electric";
          break;
        case "sparkle":
          particleStyle = {
            left: `${startX}%`,
            top: `${startY}%`,
            animationDelay: `${delay}ms`,
            animationDuration: `${duration}ms`,
          };
          particleClass = "particle-sparkle";
          break;
        case "royal":
          particleStyle = {
            left: `${startX}%`,
            top: `${startY}%`,
            animationDelay: `${delay}ms`,
            animationDuration: `${duration}ms`,
          };
          particleClass = i % 2 === 0 ? "particle-gold" : "particle-sparkle";
          break;
        // === NEW PREMIUM ANIMATIONS ===
        case "throne":
          particleStyle = {
            left: `${startX}%`,
            top: `${startY}%`,
            animationDelay: `${delay}ms`,
            animationDuration: `${duration}ms`,
          };
          particleClass = i % 3 === 0 ? "particle-gold" : i % 3 === 1 ? "particle-ruby" : "particle-sparkle";
          break;
        case "speed":
          particleStyle = {
            left: `${startX}%`,
            top: `${40 + Math.random() * 20}%`,
            animationDelay: `${delay}ms`,
            animationDuration: `${400 + Math.random() * 300}ms`,
          };
          particleClass = "particle-speed";
          break;
        case "phoenix":
          particleStyle = {
            left: `${30 + Math.random() * 40}%`,
            bottom: "5%",
            animationDelay: `${delay}ms`,
            animationDuration: `${1000 + Math.random() * 1000}ms`,
          };
          particleClass = i % 2 === 0 ? "particle-phoenix-flame" : "particle-feather";
          break;
        case "castle":
          particleStyle = {
            left: `${startX}%`,
            top: `${startY}%`,
            animationDelay: `${delay}ms`,
            animationDuration: `${duration}ms`,
          };
          particleClass = i % 4 === 0 ? "particle-magic" : i % 4 === 1 ? "particle-sparkle" : i % 4 === 2 ? "particle-gold" : "particle-fairy";
          break;
        case "cosmic":
          particleStyle = {
            left: `${startX}%`,
            top: `${startY}%`,
            animationDelay: `${delay}ms`,
            animationDuration: `${2000 + Math.random() * 2000}ms`,
          };
          particleClass = i % 5 === 0 ? "particle-star" : i % 5 === 1 ? "particle-planet" : i % 5 === 2 ? "particle-nebula" : i % 5 === 3 ? "particle-comet" : "particle-galaxy";
          break;
        default: // float
          particleStyle = {
            left: `${startX}%`,
            top: `${startY}%`,
            animationDelay: `${delay}ms`,
            animationDuration: `${duration}ms`,
          };
          particleClass = "particle-heart";
      }
      
      particles.push(
        <div 
          key={i} 
          className={`absolute ${particleClass}`}
          style={particleStyle}
        />
      );
    }
    return particles;
  };
  
  // Get background based on tier - TRANSPARENT for all tiers
  const getBackgroundClass = () => {
    // Only add subtle effect for high-tier gifts, but mostly transparent
    switch (gift.tier) {
      case 7: return "bg-black/30"; // Subtle dark for cosmic effect
      case 6: 
      case 5: return "bg-black/20"; // Very subtle
      default: return "bg-transparent"; // Fully transparent
    }
  };
  
  return (
    <div 
      ref={containerRef}
      className={`fixed inset-0 z-[100] pointer-events-none overflow-hidden ${getBackgroundClass()} transition-opacity duration-500 ${phase > 0 ? 'opacity-100' : 'opacity-0'}`}
    >
      {/* Particles */}
      <div className="absolute inset-0">
        {renderParticles()}
      </div>
      
      {/* Main gift animation */}
      <div className={`absolute inset-0 flex items-center justify-center`}>
        <div 
          className={`transform transition-all ${
            phase === 1 ? 'scale-0 opacity-0' : 
            phase === 2 ? 'scale-100 opacity-100 animate-gift-bounce' : 
            'scale-150 opacity-0'
          }`}
          style={{ 
            transitionDuration: '500ms',
            filter: gift.tier >= 4 ? 'drop-shadow(0 0 30px rgba(255,215,0,0.8))' : 
                   gift.tier >= 3 ? 'drop-shadow(0 0 20px rgba(255,100,0,0.6))' :
                   'drop-shadow(0 0 10px rgba(255,150,200,0.5))'
          }}
        >
          <img 
            src={gift.image} 
            alt={gift.name}
            className={`w-48 h-48 md:w-64 md:h-64 object-contain`}
            style={{ transform: `scale(${gift.animation.scale})` }}
          />
        </div>
        
        {/* Gift name and value */}
        <div 
          className={`absolute bottom-1/4 text-center transition-all duration-500 ${
            phase === 2 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
          <p className="text-3xl font-bold text-white drop-shadow-lg">{gift.name}</p>
          <p className="text-xl text-amber-300 font-semibold mt-1">
            {formatCoins(gift.cost)} ADE Coins
          </p>
        </div>
      </div>
      
      {/* Tier-specific effects */}
      {gift.tier >= 4 && (
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(255,215,0,0.3),transparent_70%)] animate-pulse" />
      )}
      {gift.tier >= 5 && (
        <>
          <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-yellow-400 to-transparent animate-shimmer" />
          <div className="absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-yellow-400 to-transparent animate-shimmer" style={{ animationDelay: '500ms' }} />
        </>
      )}
      
      {/* Particle animation styles */}
      <style>{`
        .particle-heart {
          width: 20px;
          height: 20px;
          background: radial-gradient(circle, #ff69b4 0%, #ff1493 100%);
          border-radius: 50%;
          animation: floatUp 2s ease-out forwards;
        }
        .particle-petal {
          width: 15px;
          height: 20px;
          background: linear-gradient(135deg, #ff6b6b 0%, #ee5a5a 100%);
          border-radius: 50% 0 50% 50%;
          animation: fallDown 3s ease-in-out forwards;
          transform: rotate(45deg);
        }
        .particle-flame {
          width: 10px;
          height: 30px;
          background: linear-gradient(to top, #ff4500 0%, #ffa500 50%, #ffff00 100%);
          border-radius: 50% 50% 20% 20%;
          animation: riseUp 1s ease-out forwards;
          filter: blur(2px);
        }
        .particle-electric {
          width: 3px;
          height: 40px;
          background: linear-gradient(to bottom, #00bfff, #ffffff, #00bfff);
          animation: flash 0.3s ease-in-out infinite;
          transform: rotate(${Math.random() * 360}deg);
        }
        .particle-sparkle {
          width: 8px;
          height: 8px;
          background: white;
          border-radius: 50%;
          box-shadow: 0 0 10px #fff, 0 0 20px #fff, 0 0 30px #fff;
          animation: twinkle 1s ease-in-out infinite;
        }
        .particle-gold {
          width: 12px;
          height: 12px;
          background: linear-gradient(135deg, #ffd700 0%, #ffec8b 50%, #ffd700 100%);
          border-radius: 50%;
          box-shadow: 0 0 15px rgba(255, 215, 0, 0.8);
          animation: floatAround 2s ease-in-out infinite;
        }
        
        @keyframes floatUp {
          0% { transform: translateY(0) scale(1); opacity: 1; }
          100% { transform: translateY(-200px) scale(0); opacity: 0; }
        }
        @keyframes fallDown {
          0% { transform: translateY(-100%) rotate(0deg); opacity: 1; }
          100% { transform: translateY(100vh) rotate(720deg); opacity: 0; }
        }
        @keyframes riseUp {
          0% { transform: translateY(0) scale(1); opacity: 0.8; }
          100% { transform: translateY(-150px) scale(0.3); opacity: 0; }
        }
        @keyframes flash {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.3; }
        }
        @keyframes twinkle {
          0%, 100% { transform: scale(1); opacity: 1; }
          50% { transform: scale(1.5); opacity: 0.5; }
        }
        @keyframes floatAround {
          0%, 100% { transform: translate(0, 0); }
          25% { transform: translate(10px, -10px); }
          50% { transform: translate(0, -20px); }
          75% { transform: translate(-10px, -10px); }
        }
        
        /* === NEW PREMIUM PARTICLES === */
        .particle-ruby {
          width: 10px;
          height: 10px;
          background: linear-gradient(135deg, #dc143c 0%, #ff6b6b 50%, #dc143c 100%);
          border-radius: 2px;
          transform: rotate(45deg);
          box-shadow: 0 0 15px rgba(220, 20, 60, 0.8);
          animation: floatAround 2s ease-in-out infinite;
        }
        .particle-speed {
          width: 60px;
          height: 3px;
          background: linear-gradient(to right, transparent, #ff4444, #ffaa00, transparent);
          border-radius: 2px;
          animation: speedLine 0.5s ease-out forwards;
        }
        .particle-phoenix-flame {
          width: 15px;
          height: 40px;
          background: linear-gradient(to top, #ff4500 0%, #ff8c00 30%, #ffd700 70%, #fff 100%);
          border-radius: 50% 50% 20% 20%;
          animation: phoenixRise 1.5s ease-out forwards;
          filter: blur(1px);
        }
        .particle-feather {
          width: 8px;
          height: 25px;
          background: linear-gradient(135deg, #ffd700 0%, #ff8c00 50%, #ff4500 100%);
          border-radius: 50% 50% 50% 50%;
          animation: featherFloat 2s ease-in-out forwards;
          transform: rotate(30deg);
        }
        .particle-magic {
          width: 6px;
          height: 6px;
          background: radial-gradient(circle, #00ffff 0%, #8b5cf6 100%);
          border-radius: 50%;
          box-shadow: 0 0 20px #8b5cf6, 0 0 40px #00ffff;
          animation: magicOrbit 3s linear infinite;
        }
        .particle-fairy {
          width: 4px;
          height: 4px;
          background: white;
          border-radius: 50%;
          box-shadow: 0 0 10px #fff, 0 0 20px #ffd700, 0 0 30px #ff69b4;
          animation: fairyDance 2s ease-in-out infinite;
        }
        .particle-star {
          width: 4px;
          height: 4px;
          background: white;
          border-radius: 50%;
          box-shadow: 0 0 10px #fff, 0 0 20px #fff;
          animation: starTwinkle 1.5s ease-in-out infinite;
        }
        .particle-planet {
          width: 20px;
          height: 20px;
          background: radial-gradient(circle at 30% 30%, #87ceeb 0%, #4169e1 50%, #191970 100%);
          border-radius: 50%;
          box-shadow: inset -5px -5px 10px rgba(0,0,0,0.5);
          animation: planetOrbit 4s linear infinite;
        }
        .particle-nebula {
          width: 40px;
          height: 40px;
          background: radial-gradient(ellipse, rgba(138,43,226,0.6) 0%, rgba(75,0,130,0.3) 50%, transparent 70%);
          border-radius: 50%;
          filter: blur(5px);
          animation: nebulaPulse 3s ease-in-out infinite;
        }
        .particle-comet {
          width: 8px;
          height: 8px;
          background: radial-gradient(circle, #fff 0%, #87ceeb 100%);
          border-radius: 50%;
          box-shadow: 30px 0 20px 5px rgba(135,206,235,0.5), 60px 0 30px 10px rgba(135,206,235,0.3);
          animation: cometFly 2s linear forwards;
        }
        .particle-galaxy {
          width: 30px;
          height: 30px;
          background: conic-gradient(from 0deg, #4b0082, #8b5cf6, #00bfff, #4b0082);
          border-radius: 50%;
          filter: blur(3px);
          animation: galaxySpin 4s linear infinite;
        }
        
        @keyframes speedLine {
          0% { transform: translateX(-100vw); opacity: 1; }
          100% { transform: translateX(100vw); opacity: 0; }
        }
        @keyframes phoenixRise {
          0% { transform: translateY(0) scale(1); opacity: 0.9; }
          100% { transform: translateY(-300px) scale(0.5); opacity: 0; }
        }
        @keyframes featherFloat {
          0% { transform: translateY(0) rotate(30deg); opacity: 1; }
          100% { transform: translateY(-200px) translateX(50px) rotate(180deg); opacity: 0; }
        }
        @keyframes magicOrbit {
          0% { transform: rotate(0deg) translateX(50px) rotate(0deg); }
          100% { transform: rotate(360deg) translateX(50px) rotate(-360deg); }
        }
        @keyframes fairyDance {
          0%, 100% { transform: translate(0, 0) scale(1); }
          25% { transform: translate(20px, -20px) scale(1.2); }
          50% { transform: translate(0, -30px) scale(1); }
          75% { transform: translate(-20px, -20px) scale(1.2); }
        }
        @keyframes starTwinkle {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.3; transform: scale(0.5); }
        }
        @keyframes planetOrbit {
          0% { transform: rotate(0deg) translateX(80px) rotate(0deg); }
          100% { transform: rotate(360deg) translateX(80px) rotate(-360deg); }
        }
        @keyframes nebulaPulse {
          0%, 100% { transform: scale(1); opacity: 0.6; }
          50% { transform: scale(1.3); opacity: 0.3; }
        }
        @keyframes cometFly {
          0% { transform: translate(100vw, -50vh) rotate(-45deg); }
          100% { transform: translate(-100vw, 50vh) rotate(-45deg); }
        }
        @keyframes galaxySpin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        
        .animate-gift-bounce {
          animation: giftBounce 0.5s ease-out;
        }
        @keyframes giftBounce {
          0% { transform: scale(0.5); }
          50% { transform: scale(1.1); }
          70% { transform: scale(0.95); }
          100% { transform: scale(1); }
        }
        
        .animate-shimmer {
          animation: shimmer 2s linear infinite;
        }
        @keyframes shimmer {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
      `}</style>
    </div>
  );
};

// ===== ANIMATED GIFT CARD COMPONENT =====
// Gift card in the selection grid with hover animation
const GiftCard = ({ gift, isSelected, onClick, canAfford, goldenLevel = 0 }) => {
  const [isHovered, setIsHovered] = useState(false);
  const isLocked = gift.premium && goldenLevel < gift.premium;
  
  return (
    <button
      onClick={() => !isLocked && onClick()}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      disabled={isLocked}
      className={`relative flex flex-col items-center p-3 rounded-xl transition-all duration-300 ${
        isSelected 
          ? 'bg-gradient-to-br from-amber-100 to-orange-100 ring-2 ring-amber-400 ring-offset-2 scale-105' 
          : isLocked
            ? 'bg-gray-100 opacity-60 cursor-not-allowed'
            : 'bg-white hover:bg-amber-50 hover:shadow-lg hover:scale-105'
      } ${!canAfford && !isLocked ? 'opacity-50' : ''}`}
    >
      {/* Gift image with animation */}
      <div 
        className={`relative w-16 h-16 transition-transform duration-300 ${isHovered && !isLocked ? 'animate-wiggle' : ''}`}
      >
        <img 
          src={gift.image} 
          alt={gift.name}
          className="w-full h-full object-contain drop-shadow-lg"
        />
        {/* Glow effect on hover */}
        {isHovered && !isLocked && (
          <div 
            className="absolute inset-0 rounded-full blur-xl opacity-50 -z-10"
            style={{ 
              background: gift.tier >= 4 ? 'radial-gradient(circle, rgba(255,215,0,0.6), transparent)' :
                         gift.tier >= 3 ? 'radial-gradient(circle, rgba(255,100,0,0.5), transparent)' :
                         'radial-gradient(circle, rgba(255,150,200,0.5), transparent)'
            }}
          />
        )}
      </div>
      
      {/* Gift name */}
      <span className="text-xs font-semibold text-gray-700 mt-1">{gift.name}</span>
      
      {/* Cost */}
      <div className="flex items-center gap-1 mt-0.5">
        <img src="/images/ade-logo-extracted.png" alt="" className="w-3 h-3 object-contain" />
        <span className={`text-xs font-bold ${canAfford || isLocked ? 'text-amber-600' : 'text-red-500'}`}>
          {formatCoins(gift.cost)}
        </span>
      </div>
      
      {/* Premium badge */}
      {gift.premium && (
        <span className="absolute -top-1 -right-1 bg-gradient-to-r from-yellow-400 to-amber-500 text-white text-[8px] font-bold px-1.5 py-0.5 rounded-full flex items-center gap-0.5 shadow-lg">
          <Crown className="w-2 h-2" />
          Lv.{gift.premium}
        </span>
      )}
      
      {/* Lock overlay */}
      {isLocked && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/40 rounded-xl">
          <span className="text-white text-xs font-bold">🔒 Gold Lv.{gift.premium}</span>
        </div>
      )}
      
      {/* Wiggle animation */}
      <style>{`
        .animate-wiggle {
          animation: wiggle 0.5s ease-in-out infinite;
        }
        @keyframes wiggle {
          0%, 100% { transform: rotate(-3deg); }
          50% { transform: rotate(3deg); }
        }
      `}</style>
    </button>
  );
};

// ===== GIFT BUTTON COMPONENT =====
// Shows under photos to open gift modal
export const GiftButton = ({ 
  photoId, 
  recipientId,
  recipientName,
  totalGifts = 0, 
  onGiftSent,
  size = "md" 
}) => {
  const [showPicker, setShowPicker] = useState(false);
  
  return (
    <>
      <button
        onClick={() => setShowPicker(true)}
        className="flex items-center gap-1.5 group transition-all"
        title="Invia regalo"
        data-testid={`gift-btn-${photoId || recipientId}`}
      >
        <div className="relative">
          <Gift 
            className={`${size === 'sm' ? 'w-5 h-5' : 'w-7 h-7'} text-amber-500 group-hover:text-amber-600 transition-colors group-hover:scale-110 group-active:scale-95`}
          />
          <Sparkles className="absolute -top-1 -right-1 w-3 h-3 text-amber-400 opacity-0 group-hover:opacity-100 transition-opacity animate-pulse" />
        </div>
        {totalGifts > 0 && (
          <span className="text-sm font-bold bg-gradient-to-r from-amber-500 to-orange-500 bg-clip-text text-transparent">
            {formatCoins(totalGifts)}
          </span>
        )}
      </button>
      
      <GiftPickerV2
        isOpen={showPicker}
        onClose={() => setShowPicker(false)}
        recipientId={recipientId}
        recipientName={recipientName}
        photoId={photoId}
        onGiftSent={(data) => {
          onGiftSent?.(1, data);
        }}
      />
    </>
  );
};

// ===== MAIN GIFT MODAL COMPONENT =====
export const GiftModal = ({ 
  isOpen, 
  onClose, 
  photoId = null, 
  recipientId, 
  recipientName = "questo utente",
  onGiftSent 
}) => {
  const [wallet, setWallet] = useState({ balance: 0, golden: { level: 0 } });
  const [selectedGift, setSelectedGift] = useState(null);
  const [sending, setSending] = useState(false);
  const [showAnimation, setShowAnimation] = useState(false);
  const [sentGift, setSentGift] = useState(null);
  
  useEffect(() => {
    if (isOpen) {
      fetchWallet();
      setSelectedGift(null);
    }
  }, [isOpen]);
  
  const fetchWallet = async () => {
    try {
      const response = await api.get('/wallet');
      setWallet(response.data);
    } catch (error) {
      console.error('Error fetching wallet:', error);
    }
  };
  
  const handleSendGift = async () => {
    if (!selectedGift) {
      toast.error("Seleziona un regalo");
      return;
    }
    
    if (wallet.balance < selectedGift.cost) {
      toast.error("Saldo insufficiente");
      return;
    }
    
    setSending(true);
    try {
      const payload = {
        recipient_id: recipientId,
        amount: selectedGift.cost,
        gift_id: selectedGift.id,
        photo_id: photoId
      };
      
      await api.post('/gifts/send', payload);
      
      // Play success sound based on tier
      playGiftSound(selectedGift.tier);
      
      // Store sent gift for animation
      setSentGift(selectedGift);
      
      // Close modal first
      onClose();
      
      // Show full-screen animation
      setTimeout(() => setShowAnimation(true), 100);
      
      // Update wallet locally
      setWallet(prev => ({ ...prev, balance: prev.balance - selectedGift.cost }));
      
      if (onGiftSent) {
        onGiftSent(selectedGift.cost);
      }
      
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nell'invio del regalo");
    } finally {
      setSending(false);
    }
  };
  
  const handleAnimationComplete = () => {
    setShowAnimation(false);
    setSentGift(null);
    toast.success(`${selectedGift?.name} inviato con successo!`);
    setSelectedGift(null);
  };
  
  const goldenLevel = wallet.golden?.level || 0;
  
  // Separate standard and premium gifts
  const standardGifts = ADE_GIFTS.filter(g => !g.premium);
  const premiumGifts = ADE_GIFTS.filter(g => g.premium);
  
  return (
    <>
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="max-w-md mx-auto bg-gradient-to-br from-white via-amber-50/30 to-orange-50/30 border-amber-200 p-0 overflow-hidden">
          <DialogHeader className="p-4 pb-2">
            <DialogTitle className="flex items-center gap-2 text-xl">
              <Gift className="w-6 h-6 text-amber-500" />
              <span className="bg-gradient-to-r from-amber-500 to-orange-500 bg-clip-text text-transparent font-bold">
                Invia Regalo
              </span>
            </DialogTitle>
          </DialogHeader>
          
          <div className="px-4 pb-4 space-y-4 max-h-[70vh] overflow-y-auto">
            {/* Wallet Balance */}
            <div className="flex items-center justify-between p-3 bg-gradient-to-r from-amber-100 to-orange-100 rounded-xl">
              <div>
                <span className="text-xs text-amber-600">Il tuo saldo:</span>
                <div className="flex items-center gap-1.5">
                  <img src="/images/ade-logo-extracted.png" alt="ADE" className="w-5 h-5 object-contain" />
                  <span className="font-bold text-amber-700 text-lg">{formatCoins(wallet.balance)}</span>
                </div>
              </div>
              <button
                onClick={() => window.location.href = '/app/profile?shop=true'}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-gradient-to-r from-amber-500 to-orange-500 text-white text-xs font-bold rounded-full shadow-lg hover:shadow-xl transition-shadow"
              >
                <span>+ Ricarica</span>
              </button>
            </div>
            
            {/* Standard Gifts Grid */}
            <div>
              <h4 className="text-xs text-gray-500 uppercase tracking-wide mb-2 font-semibold">
                Regali
              </h4>
              <div className="grid grid-cols-4 gap-2">
                {standardGifts.map((gift) => (
                  <GiftCard
                    key={gift.id}
                    gift={gift}
                    isSelected={selectedGift?.id === gift.id}
                    onClick={() => setSelectedGift(gift)}
                    canAfford={wallet.balance >= gift.cost}
                    goldenLevel={goldenLevel}
                  />
                ))}
              </div>
            </div>
            
            {/* Premium Gifts */}
            {premiumGifts.length > 0 && (
              <div>
                <h4 className="text-xs text-gray-500 uppercase tracking-wide mb-2 font-semibold flex items-center gap-1">
                  <Crown className="w-3 h-3 text-yellow-500" />
                  Regali Premium
                </h4>
                <div className="grid grid-cols-3 gap-2">
                  {premiumGifts.map((gift) => (
                    <GiftCard
                      key={gift.id}
                      gift={gift}
                      isSelected={selectedGift?.id === gift.id}
                      onClick={() => setSelectedGift(gift)}
                      canAfford={wallet.balance >= gift.cost}
                      goldenLevel={goldenLevel}
                    />
                  ))}
                </div>
              </div>
            )}
            
            {/* Selected Gift Preview */}
            {selectedGift && (
              <div className="flex items-center gap-4 p-3 bg-amber-50 rounded-xl border border-amber-200">
                <img 
                  src={selectedGift.image} 
                  alt={selectedGift.name}
                  className="w-16 h-16 object-contain animate-pulse"
                />
                <div className="flex-1">
                  <p className="font-bold text-gray-800">{selectedGift.name}</p>
                  <p className="text-xs text-gray-500">{selectedGift.description}</p>
                  <p className="text-lg font-bold text-amber-600 mt-1">
                    {formatCoins(selectedGift.cost)} ADE Coins
                  </p>
                </div>
              </div>
            )}
            
            {/* Send Button */}
            <Button
              onClick={handleSendGift}
              disabled={!selectedGift || sending || wallet.balance < (selectedGift?.cost || 0)}
              className="w-full bg-gradient-to-r from-amber-500 via-orange-500 to-red-500 text-white font-bold py-3 rounded-xl shadow-lg shadow-orange-200/50 disabled:opacity-50"
            >
              {sending ? (
                <span className="flex items-center gap-2">
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Invio...
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <Gift className="w-5 h-5" />
                  Invia Regalo
                </span>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Full-screen 3D gift animation */}
      <Gift3DAnimation
        gift={sentGift}
        isVisible={showAnimation}
        onComplete={handleAnimationComplete}
      />
    </>
  );
};

// ===== GIFT VALUE COUNTER =====
export const GiftValueCounter = ({ totalValue, size = "md" }) => {
  if (!totalValue || totalValue === 0) return null;
  
  const sizeClasses = {
    sm: "text-xs",
    md: "text-sm",
    lg: "text-base"
  };
  
  return (
    <div className="flex items-center gap-1.5 bg-gradient-to-r from-amber-100 to-orange-100 px-2.5 py-1 rounded-full">
      <img src="/images/ade-logo-extracted.png" alt="ADE" className={`${size === 'sm' ? 'w-3 h-3' : 'w-4 h-4'} object-contain`} />
      <span className={`font-bold bg-gradient-to-r from-amber-600 to-orange-600 bg-clip-text text-transparent ${sizeClasses[size]}`}>
        {formatCoins(totalValue)}
      </span>
      <Sparkles className={`text-amber-500 ${size === 'sm' ? 'w-3 h-3' : 'w-4 h-4'}`} />
    </div>
  );
};

// ===== SOUND EFFECTS =====
const playGiftSound = (tier) => {
  try {
    const AudioContext = window.AudioContext || window.webkitAudioContext;
    if (!AudioContext) return;
    
    const ctx = new AudioContext();
    
    // Different melodies based on tier
    let notes;
    switch (tier) {
      case 5: // Royal - triumphant fanfare
        notes = [523.25, 659.25, 783.99, 1046.5, 1318.51, 1567.98];
        break;
      case 4: // Diamond - magical sparkle
        notes = [987.77, 1174.66, 1318.51, 1567.98, 1760];
        break;
      case 3: // Fire/Lightning - powerful
        notes = [392, 493.88, 587.33, 698.46, 783.99];
        break;
      case 2: // Rose/Star - sweet
        notes = [523.25, 587.33, 659.25, 783.99];
        break;
      default: // Heart - simple
        notes = [523.25, 659.25, 783.99];
    }
    
    notes.forEach((freq, i) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      
      osc.type = tier >= 4 ? 'triangle' : 'sine';
      osc.frequency.value = freq;
      osc.connect(gain);
      gain.connect(ctx.destination);
      
      const startTime = ctx.currentTime + i * 0.12;
      const volume = tier >= 4 ? 0.15 : 0.1;
      gain.gain.setValueAtTime(volume, startTime);
      gain.gain.exponentialRampToValueAtTime(0.001, startTime + 0.4);
      
      osc.start(startTime);
      osc.stop(startTime + 0.4);
    });
  } catch (e) {
    console.log('Sound not supported');
  }
};

// Connection sounds (used elsewhere)
export const playConnectionSound = (type) => {
  try {
    const frequencies = {
      send: [523.25, 659.25, 783.99],
      accept: [783.99, 659.25, 523.25, 783.99],
      cancel: [392, 349.23],
    };
    
    const AudioContext = window.AudioContext || window.webkitAudioContext;
    if (!AudioContext) return;
    
    const ctx = new AudioContext();
    const notes = frequencies[type] || frequencies.send;
    
    notes.forEach((freq, i) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      
      osc.type = 'sine';
      osc.frequency.value = freq;
      osc.connect(gain);
      gain.connect(ctx.destination);
      
      const startTime = ctx.currentTime + i * 0.15;
      gain.gain.setValueAtTime(0.1, startTime);
      gain.gain.exponentialRampToValueAtTime(0.001, startTime + 0.3);
      
      osc.start(startTime);
      osc.stop(startTime + 0.3);
    });
  } catch (e) {
    console.log('Sound not supported');
  }
};

export default GiftModal;
